import {
  getOptJobLatestKey,
  OptJobStatus,
  OptJobType,
  TERMINAL_OPT_JOB_STATUSES,
  useOptJobMutation,
} from 'api/optJobs/optJobs.api'
import { queryClient } from 'helpers/queryClient'
import type { Channel } from 'pusher-js'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { useEvent } from 'ui/components/PusherContext/PusherContext'
import Datetime from 'utils/datetime/datetime'
import { hasAllPriceValues, isIntradayOptJobActive } from '../../IntradayTradingView.helpers'
import type { HourInput } from '../../IntradayTradingView.types'

export const INTRADAY_PLAN_OPT_JOB_TYPE: OptJobType = 'intraday_plan'
const OPTIMIZATION_UPDATED_EVENT = 'optimization-updated'

type IntradayPlanOptimizationUpdatedEventData = {
  system: number
  opt_job_type: string
  status: OptJobStatus
  opt_job_id: number
  subtype?: string
}

function getIntradayAsyncFailureFeedbackMessage(status: OptJobStatus): string {
  if (status === 'Unsolvable') {
    return 'Optimization request was created, but processing ended as unsolvable. Adjust input values and try again.'
  }

  return 'Optimization request was created, but processing failed before completion. Please try again or contact support.'
}

function getOptimizationWindow(optimizationPeriodHours: number): { optStartTime: string; optEndTime: string } {
  const start = new Date()
  start.setMinutes(0, 0, 0)
  const end = new Date(start)
  end.setHours(start.getHours() + optimizationPeriodHours)

  return {
    optStartTime: Datetime.toISOString(start),
    optEndTime: Datetime.toISOString(end),
  }
}

function buildIntraDayPriceInputData(prices: HourInput[]): {
  intraday_prices: { times: string[]; values: number[] }
} {
  const times: string[] = []
  const values: number[] = []

  prices.forEach((price, index) => {
    const trimmedValue = price.value.trim()
    if (trimmedValue === '') {
      return
    }

    const rowStart = new Date()
    rowStart.setMinutes(0, 0, 0)
    rowStart.setHours(rowStart.getHours() + index + 1)

    times.push(Datetime.toISOString(rowStart))
    values.push(parseFloat(trimmedValue.replace(',', '.')))
  })

  return {
    intraday_prices: {
      times,
      values,
    },
  }
}

function getCreatedOptJobId(response: unknown): number | null {
  if (typeof response !== 'object' || response === null) {
    return null
  }

  const maybeId = (response as { id?: unknown }).id
  return typeof maybeId === 'number' ? maybeId : null
}

function invalidateLatestIntradayOptJobQuery({
  systemId,
  status,
  subtypes,
}: {
  systemId?: number
  status?: OptJobStatus
  subtypes?: string[]
} = {}): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: getOptJobLatestKey({ systemId: systemId, type: INTRADAY_PLAN_OPT_JOB_TYPE, limit: 1, status, subtypes }),
    exact: true,
    refetchType: 'active',
  })
}

type UseIntradayOptimizationParams = {
  systemId: number | undefined
  activeSubtype: string
  inputPrices: HourInput[]
  setInputPrices: Dispatch<SetStateAction<HourInput[]>>
  optimizationsChannel: Channel | null
  latestIntradayOptJobId: number | null
  latestIntradayOptJobStatus: OptJobStatus | null
  optimizationPeriodHours: number
}

type UseIntradayOptimizationResult = {
  datasetRefreshToken: string
  isOptimizing: boolean
  showOptimizationIsRunning: boolean
  canRunOptimization: boolean
  handleRunTestOptimization: () => void
  resetOptimizationTracking: () => void
}

export function useIntradayOptimization({
  systemId,
  activeSubtype,
  inputPrices,
  setInputPrices,
  optimizationsChannel,
  latestIntradayOptJobId,
  latestIntradayOptJobStatus,
  optimizationPeriodHours,
}: UseIntradayOptimizationParams): UseIntradayOptimizationResult {
  const { t } = useTranslation()
  const { mutate: createOptJob } = useOptJobMutation()
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [pendingOptimizationJobId, setPendingOptimizationJobId] = useState<number | null>(null)
  const [datasetRefreshToken, setDatasetRefreshToken] = useState('intraday-view-init')

  const canRunOptimization = hasAllPriceValues(inputPrices)
  const showOptimizationIsRunning = isOptimizing || isIntradayOptJobActive(latestIntradayOptJobStatus)

  useEvent(
    optimizationsChannel,
    OPTIMIZATION_UPDATED_EVENT,
    (data: IntradayPlanOptimizationUpdatedEventData | undefined): void => {
      if (!data) {
        return
      }

      if (data.opt_job_type !== INTRADAY_PLAN_OPT_JOB_TYPE) {
        return
      }

      if (systemId && data.system !== systemId) {
        return
      }

      const isLocalPendingJob = pendingOptimizationJobId !== null && data.opt_job_id === pendingOptimizationJobId
      const eventSubtype = data.subtype

      if (activeSubtype && eventSubtype !== undefined && eventSubtype !== activeSubtype) {
        // Keep non-active subtype caches fresh so switching back reflects current backend status.
        invalidateLatestIntradayOptJobQuery({
          systemId,
          subtypes: [eventSubtype],
        })

        if (data.status === 'Finished') {
          invalidateLatestIntradayOptJobQuery({
            systemId,
            status: 'Finished',
            subtypes: [eventSubtype],
          })
        }

        return
      }

      invalidateLatestIntradayOptJobQuery({
        systemId,
        subtypes: activeSubtype ? [activeSubtype] : undefined,
      })

      if (data.status === 'Finished') {
        invalidateLatestIntradayOptJobQuery({
          systemId,
          status: 'Finished',
          subtypes: activeSubtype ? [activeSubtype] : undefined,
        })

        if (!isLocalPendingJob) {
          setDatasetRefreshToken(`intraday-view-optimize-${Date.now()}`)
        }
      }

      if (!isLocalPendingJob) {
        return
      }

      if (data.status === 'Finished') {
        setDatasetRefreshToken(`intraday-view-optimize-${Date.now()}`)
        setIsOptimizing(false)
        setPendingOptimizationJobId(null)
        alertStore.success(t('Optimization result updated.'), {
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 6000,
        })
      } else if (data.status === 'Failed' || data.status === 'Unsolvable') {
        setIsOptimizing(false)
        setPendingOptimizationJobId(null)
        alertStore.error(t(getIntradayAsyncFailureFeedbackMessage(data.status)), {
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 6000,
        })
      }
    }
  )

  useEffect(() => {
    if (!isOptimizing || pendingOptimizationJobId === null) {
      return
    }

    if (latestIntradayOptJobId !== pendingOptimizationJobId) {
      return
    }

    if (!latestIntradayOptJobStatus || !TERMINAL_OPT_JOB_STATUSES.has(latestIntradayOptJobStatus)) {
      return
    }

    if (latestIntradayOptJobStatus === 'Finished') {
      setDatasetRefreshToken(`intraday-view-optimize-${Date.now()}`)
      alertStore.success(t('Optimization result updated.'), {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 6000,
      })
    } else {
      alertStore.error(t(getIntradayAsyncFailureFeedbackMessage(latestIntradayOptJobStatus)), {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 6000,
      })
    }

    setIsOptimizing(false)
    setPendingOptimizationJobId(null)
  }, [isOptimizing, pendingOptimizationJobId, latestIntradayOptJobId, latestIntradayOptJobStatus, t])

  function resetOptimizationTracking(): void {
    setIsOptimizing(false)
    setPendingOptimizationJobId(null)
  }

  function handleRunTestOptimization(): void {
    if (!canRunOptimization || showOptimizationIsRunning) {
      return
    }

    setIsOptimizing(true)
    setPendingOptimizationJobId(null)

    const intradayPriceInputData = buildIntraDayPriceInputData(inputPrices)
    const { optStartTime, optEndTime } = getOptimizationWindow(optimizationPeriodHours)

    createOptJob(
      {
        opt_job_type: INTRADAY_PLAN_OPT_JOB_TYPE,
        input_data: {
          opt_start_time: optStartTime,
          opt_end_time: optEndTime,
          ...(activeSubtype ? { include_subtypes: [activeSubtype] } : {}),
          ...intradayPriceInputData,
        },
      },
      {
        onSuccess: (createdOptJob) => {
          const createdOptJobId = getCreatedOptJobId(createdOptJob)
          if (createdOptJobId === null) {
            setIsOptimizing(false)
            setPendingOptimizationJobId(null)
            alertStore.error(t('Optimization could not be tracked. Please try again or contact support.'), {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              autoHideDuration: 6000,
            })
            return
          }

          // Keep row structure but clear entered values only after a job was successfully created.
          setInputPrices((previous) => previous.map((price) => ({ ...price, value: '' })))
          setPendingOptimizationJobId(createdOptJobId)
          invalidateLatestIntradayOptJobQuery({
            systemId,
            subtypes: activeSubtype ? [activeSubtype] : undefined,
          })
        },
        onError: () => {
          setIsOptimizing(false)
          setPendingOptimizationJobId(null)
        },
      }
    )
  }

  return {
    datasetRefreshToken,
    isOptimizing,
    showOptimizationIsRunning,
    canRunOptimization,
    handleRunTestOptimization,
    resetOptimizationTracking,
  }
}
